import {
  acts as originalActs,
  works as principalActs,
  versions as sourceActs,
  objects as originalObjects,
} from '../../../data/original_pdfs/catalog.json';

interface OriginalAct {
  sha256: string;
  title: string;
}
const acts: Record<string, OriginalAct> = originalActs;
const works: Record<string, string> = principalActs;
const versions: Record<string, string> = sourceActs;
const objects: Record<string, { filename: string; sources: string[] }> = originalObjects;

export interface OriginalPdf {
  href: string;
  title: string;
}
export function originalPdfForAct(actId?: string | null): OriginalPdf | null {
  const act = actId ? acts[actId] : undefined;
  return act
    ? { href: `/api/v1/original-pdfs/${act.sha256}.pdf`, title: act.title }
    : null;
}
// Notes may carry the engine's document ID rather than a release-specific Act ID.
// Resolve only a unique, catalog-verified PDF; never guess from an Act title.
export function originalPdfForSourceDocument(documentId: unknown): OriginalPdf | null {
  if (typeof documentId !== 'string' || !/^[a-f0-9]{16}$/.test(documentId)) return null;
  const matches = Object.entries(acts)
    .filter(([id]) => id.endsWith(`-${documentId}`))
    .map(([, act]) => act);
  if (!matches.length || new Set(matches.map((act) => act.sha256)).size !== 1)
    return null;
  return {
    href: `/api/v1/original-pdfs/${matches[0]!.sha256}.pdf`,
    title: matches[0]!.title,
  };
}
// Published notes sometimes identify an original by its repository filename.
// Match the complete basename in the verified catalog, never a partial title.
export function originalPdfForSourceFile(filename: unknown): OriginalPdf | null {
  if (
    typeof filename !== 'string' ||
    !filename.endsWith('.pdf') ||
    /[/\\]/.test(filename)
  )
    return null;
  const matches = Object.entries(objects).filter(
    ([digest, obj]) =>
      filename === `${digest}.pdf` ||
      obj.filename === filename ||
      obj.sources.some((source) => source.split('/').at(-1) === filename),
  );
  if (matches.length !== 1) return null;
  return { href: `/api/v1/original-pdfs/${matches[0]![0]}.pdf`, title: filename };
}
export function originalPdfForWork(workId: string): OriginalPdf | null {
  return originalPdfForAct(works[workId]);
}
// A version's source is its actual principal/amendment Act, never a generated
// consolidation. Unknown or future identities must not point to a guessed PDF.
export function originalPdfForVersion(versionId: string): OriginalPdf | null {
  return originalPdfForAct(versions[versionId]);
}
